import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ComparableGroupDto, GroupMemberDto, ListResponse } from "@reina/shared";
import type { MemberReviewInput } from "@reina/shared";
import { api } from "./client";

/**
 * Comparables hooks (contract §Products & comparables). Root key
 * `["comparable-groups"]` matches api/stats.ts so the dashboard count refreshes
 * with every rebuild/review.
 */

const GroupList = ListResponse(ComparableGroupDto);

export type ComparableGroupParams = {
  domain?: string;
  search?: string;
  needsReview?: boolean;
  limit?: number;
  offset?: number;
};

export function useComparableGroups(params: ComparableGroupParams = { limit: 50 }) {
  return useQuery({
    queryKey: ["comparable-groups", "list", params],
    queryFn: () => api.get("/comparables/groups", { query: params, schema: GroupList }),
  });
}

/** `POST /api/comparables/rebuild` response. */
const RebuildResult = z.object({
  groups: z.number().int(),
  members: z.number().int(),
});
export type RebuildResult = z.infer<typeof RebuildResult>;

export function useRebuildComparables() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/comparables/rebuild", undefined, { schema: RebuildResult }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["comparable-groups"] }),
  });
}

export function useReviewMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, input }: { memberId: string; input: MemberReviewInput }) =>
      api.patch(`/comparables/members/${memberId}`, input, { schema: GroupMemberDto }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["comparable-groups"] }),
  });
}
